import { cn } from "@/shared/lib/utils";

export interface ProgressSegment {
  key: string;
  label: string;
  count: number;
  colorClass: string;
}

interface Props {
  segments: ProgressSegment[];
  total: number;
  onSegmentClick?: (key: string) => void;
  /** When false, render only the bar (the caller supplies its own labels). */
  showLegend?: boolean;
}

/**
 * One horizontal bar representing the whole portfolio: each segment's width is its
 * share of `total`. Segments and legend items are buttons so clicking either filters
 * the table to that stage.
 */
export function SegmentedProgressBar({ segments, total, onSegmentClick, showLegend = true }: Props) {
  const safeTotal = Math.max(total, 1); // total === 0 → all widths resolve to 0%

  return (
    <div>
      <div
        className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-muted"
        role="group"
        aria-label="Portfolio progress by stage"
      >
        {segments
          .filter((s) => s.count > 0)
          .map((s) => (
            <button
              key={s.key}
              type="button"
              data-testid="progress-segment"
              data-stage={s.key}
              title={`${s.label}: ${s.count}`}
              aria-label={`${s.label}: ${s.count} — filter to this stage`}
              onClick={() => onSegmentClick?.(s.key)}
              className={cn("h-full transition-all hover:opacity-80", s.colorClass)}
              style={{ width: `${(s.count / safeTotal) * 100}%` }}
            />
          ))}
      </div>

      {showLegend && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {segments.map((s) => (
            <button
              key={s.key}
              type="button"
              data-testid="progress-legend-item"
              data-stage={s.key}
              onClick={() => onSegmentClick?.(s.key)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <span className={cn("h-2.5 w-2.5 rounded-sm", s.colorClass)} />
              <span className="font-semibold text-foreground">{s.count}</span>
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
